import {EnvTypes, withSystem} from '@packages/systems/wfdl';
import {functionOf, TText} from '@packages/systems/wfdl-ast';
import {
  BooleanTag,
  FALSE,
  toBooleanTag,
  TPredicate,
  TRUE,
} from '@packages/systems/plugins-utils/Boolean';
import {TCaseSensitiveText} from './CaseSensitiveText';

type CaseSensitiveText = {caseSensitive: BooleanTag; text: string};

export const TCaseSensitiveTextPredicate = functionOf(
  TCaseSensitiveText,
  TPredicate(TText)
);

export const CaseEquals: EnvTypes.UnsafeTypedValue = {
  envType: {
    dataType: withSystem({label: 'is'}, TCaseSensitiveTextPredicate),
    exported: 'visible',
  },
  value:
    ({text: text1, caseSensitive}: CaseSensitiveText) =>
    (text2: string) => {
      if (caseSensitive.tag === 'False') {
        text1 = text1.toLowerCase();
        text2 = text2.toLowerCase();
      }
      return toBooleanTag(text1 === text2);
    },
};

export const CaseDoesNotEqual: EnvTypes.UnsafeTypedValue = {
  envType: {
    dataType: withSystem({label: 'is not'}, TCaseSensitiveTextPredicate),
    exported: 'visible',
  },
  value:
    ({text: text1, caseSensitive}: CaseSensitiveText) =>
    (text2: string) => {
      if (caseSensitive.tag === 'False') {
        text1 = text1.toLowerCase();
        text2 = text2.toLowerCase();
      }
      return toBooleanTag(text1 !== text2);
    },
};

export const CaseContains: EnvTypes.UnsafeTypedValue = {
  envType: {
    dataType: withSystem({label: 'contains'}, TCaseSensitiveTextPredicate),
    exported: 'visible',
  },
  value:
    ({text: search, caseSensitive}: CaseSensitiveText) =>
    (textToSearch: string) => {
      if (caseSensitive.tag === 'False') {
        search = search.toLowerCase();
        textToSearch = textToSearch.toLowerCase();
      }
      return toBooleanTag(textToSearch.indexOf(search) !== -1);
    },
};

export const CaseDoesNotContain: EnvTypes.UnsafeTypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'does not contain'},
      TCaseSensitiveTextPredicate
    ),
    exported: 'visible',
  },
  value:
    ({text: search, caseSensitive}: CaseSensitiveText) =>
    (textToSearch: string) => {
      if (caseSensitive.tag === 'False') {
        search = search.toLowerCase();
        textToSearch = textToSearch.toLowerCase();
      }
      return toBooleanTag(textToSearch.indexOf(search) === -1);
    },
};

export const CaseStartsWith: EnvTypes.UnsafeTypedValue = {
  envType: {
    dataType: withSystem({label: 'starts with'}, TCaseSensitiveTextPredicate),
    exported: 'visible',
  },
  value:
    ({text: target, caseSensitive}: CaseSensitiveText) =>
    (text: string) => {
      let textStart = text.slice(0, target.length);
      if (caseSensitive.tag === 'False') {
        target = target.toLowerCase();
        textStart = textStart.toLowerCase();
      }
      return toBooleanTag(textStart === target);
    },
};

export const CaseDoesNotStartWith: EnvTypes.UnsafeTypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'does not start with'},
      TCaseSensitiveTextPredicate
    ),
    exported: 'visible',
  },
  value:
    ({text: target, caseSensitive}: CaseSensitiveText) =>
    (text: string) => {
      let textStart = text.slice(0, target.length);
      if (caseSensitive.tag === 'False') {
        target = target.toLowerCase();
        textStart = textStart.toLowerCase();
      }
      return toBooleanTag(textStart !== target);
    },
};

export const CaseEndsWith: EnvTypes.UnsafeTypedValue = {
  envType: {
    dataType: withSystem({label: 'ends with'}, TCaseSensitiveTextPredicate),
    exported: 'visible',
  },
  value:
    ({text: target, caseSensitive}: CaseSensitiveText) =>
    (text: string) => {
      if (target.length > text.length) {
        return FALSE;
      }
      let textEnd = text.slice(text.length - target.length);
      if (caseSensitive.tag === 'False') {
        target = target.toLowerCase();
        textEnd = textEnd.toLowerCase();
      }
      return toBooleanTag(textEnd === target);
    },
};

export const CaseDoesNotEndWith: EnvTypes.UnsafeTypedValue = {
  envType: {
    dataType: withSystem(
      {label: 'does not end with'},
      TCaseSensitiveTextPredicate
    ),
    exported: 'visible',
  },
  value:
    ({text: target, caseSensitive}: CaseSensitiveText) =>
    (text: string) => {
      if (target.length > text.length) {
        return TRUE;
      }
      let textEnd = text.slice(text.length - target.length);
      if (caseSensitive.tag === 'False') {
        target = target.toLowerCase();
        textEnd = textEnd.toLowerCase();
      }
      return toBooleanTag(textEnd !== target);
    },
};
